import * as Sentry from '@sentry/react-native';
import { isAxiosError, isCancel } from 'axios';
import Constants from 'src/core/utils/Constants';

const SELF_HANDLED_STATUSES: number[] = [
	Constants.httpStatusUnauthorized,
	Constants.httpStatusUnprocessableEntity,
	Constants.httpStatusTooManyRequests,
];

const failureStatus = (error: unknown): number | undefined =>
	isAxiosError(error) ? error.response?.status : undefined;

// Without a status the request never reached the server: offline, timed out, dns or superseded.
const isTransportFailure = (error: unknown): boolean =>
	isCancel(error) || (isAxiosError(error) && failureStatus(error) === undefined);

const isSelfHandledStatus = (status: number | undefined): boolean =>
	status !== undefined && SELF_HANDLED_STATUSES.includes(status);

class CrashlyticsService {
	captureEvent = ({
		message,
		extra,
		reportHandledStatuses = false,
	}: {
		message: string;
		extra?: Record<string, unknown>;
		reportHandledStatuses?: boolean;
	}) => {
		const error = extra?.['error'];

		if (isTransportFailure(error)) {
			return;
		}

		const status = failureStatus(error);

		if (!reportHandledStatuses && isSelfHandledStatus(status)) {
			return;
		}

		Sentry.captureEvent({
			level: 'error',
			message,
			...(status === undefined ? {} : { tags: { 'api.status': status }, fingerprint: [message, String(status)] }),
			...(extra ? { extra } : {}),
		});
	};

	setUser = ({ id }: { id?: number | string }) => {
		Sentry.setUser({ ...(id === undefined ? {} : { id }) });
	};

	clearUser = () => {
		Sentry.setUser(null);
	};
}

export default new CrashlyticsService();
